import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { getCollapsedLabel, groupAmenities, mergeUnitAndBuildingGroups } from '../utils/amenityGrouping';
import type { AmenityParentGroup, AmenitySubGroup, GroupedAmenity } from '../utils/amenityGrouping';
import { getSubGroupIcon } from '../icons';
import { Tabs } from '../../../../ui/Tabs';
import { Badge } from '../../../../ui/Badge';
import { parseLeaseTerms } from '../../CostAndFeesModule/LeaseTermsInline';
import {
  amenityItem,
  amenityList,
  leaseTermPill,
  leaseTermsContainer,
  policyList,
  policyRowInner,
  policyValue,
  spokeContentContainer,
  subGroupCard,
  subGroupIcon,
  subGroupsContainer,
  subGroupTitle,
} from './amenitiesSpoke.styles';

const DEFAULT_TAB_ORDER: Array<AmenityParentGroup> = ['Unit', 'Building', 'Policies'];

type PetPolicyData = {
  allowed: boolean;
  deposit: string | null;
  oneTimeFee: string | null;
  monthlyFee: string | null;
  maxNumberAllowed: string | null;
  maxWeightAllowed: string | null;
  comment: string;
  restriction: string;
};

type PetPoliciesMap = Record<string, PetPolicyData | undefined>;

type HighlightData = { id: string; display: string; types: Array<string> };

export type PolicyRow = {
  label: string;
  value?: string;
  badgeTone?: 'primary' | 'alert';
  badgeText?: string;
};

const extractNumber = (s: string | null): string | null => {
  if (!s) return null;
  const match = s.match(/^(\d+)/);
  return match ? match[1] : null;
};

export const buildPetPolicyRows = (petPolicies: PetPoliciesMap): Array<PolicyRow> => {
  const dog = petPolicies.dog;
  const cat = petPolicies.cat;

  if (!dog && !cat) return [];

  const rows: Array<PolicyRow> = [];

  const dogAllowed = dog?.allowed ?? false;
  const catAllowed = cat?.allowed ?? false;

  if (dog && cat) {
    if (dogAllowed === catAllowed) {
      rows.push({
        label: 'Dogs & cats',
        badgeTone: dogAllowed ? 'primary' : 'alert',
        badgeText: dogAllowed ? 'Allowed' : 'Not allowed',
      });
    } else {
      rows.push({
        label: 'Dogs',
        badgeTone: dogAllowed ? 'primary' : 'alert',
        badgeText: dogAllowed ? 'Allowed' : 'Not allowed',
      });
      rows.push({
        label: 'Cats',
        badgeTone: catAllowed ? 'primary' : 'alert',
        badgeText: catAllowed ? 'Allowed' : 'Not allowed',
      });
    }
  } else if (dog) {
    rows.push({
      label: 'Dogs',
      badgeTone: dogAllowed ? 'primary' : 'alert',
      badgeText: dogAllowed ? 'Allowed' : 'Not allowed',
    });
  } else if (cat) {
    rows.push({
      label: 'Cats',
      badgeTone: catAllowed ? 'primary' : 'alert',
      badgeText: catAllowed ? 'Allowed' : 'Not allowed',
    });
  }

  const dogNum = extractNumber(dog?.maxNumberAllowed ?? null);
  const catNum = extractNumber(cat?.maxNumberAllowed ?? null);
  const maxNum = dogNum && catNum ? String(Math.max(Number(dogNum), Number(catNum))) : dogNum || catNum || null;

  if (maxNum) {
    rows.push({ label: 'Number of pets allowed', value: maxNum });
  }

  const hasFees = Boolean(
    dog?.deposit || dog?.oneTimeFee || dog?.monthlyFee || cat?.deposit || cat?.oneTimeFee || cat?.monthlyFee,
  );
  if (hasFees) {
    rows.push({ label: 'Additional pet fees apply' });
  }

  return rows;
};

const renderSubGroupCard = (sg: GroupedAmenity) => {
  const icon = getSubGroupIcon(sg.subGroup);
  return (
    <div key={sg.subGroup} className={subGroupCard}>
      <div className={subGroupTitle}>
        {icon && <span className={subGroupIcon}>{icon}</span>}
        {sg.subGroup}
      </div>
      <ul className={amenityList}>
        {sg.items.map((item) => (
          <li key={item} className={amenityItem}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AmenitiesSpokeContent = () => {
  const [activeTabGroup, setActiveTabGroup] = useState<AmenityParentGroup>('Unit');

  const amenitiesData = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing?.amenities);
  const leaseTerms = useSelector(
    (state: RootReduxState) => state.currentListingDetails?.currentListing?.details?.leaseTerms,
  );
  const propertyType = useSelector(
    (state: RootReduxState) => state.currentListingDetails?.currentListing?.propertyType as string | undefined,
  );
  const listingType = useSelector(
    (state: RootReduxState) => state.currentListingDetails?.currentListing?.listingType as string | undefined,
  );

  const rawAmenities = Array.isArray(amenitiesData?.amenities) ? amenitiesData.amenities : [];
  const petPolicies = (amenitiesData?.petPolicies ?? null) as PetPoliciesMap | null;
  const highlights = ((amenitiesData as unknown as Record<string, unknown>)?.highlights ?? []) as Array<HighlightData>;
  const parkingHighlight = highlights.find((h) => h.id === 'parking');
  const grouped = groupAmenities(rawAmenities);

  const collapsedLabel = getCollapsedLabel(propertyType, listingType);
  const displayGroups = collapsedLabel ? mergeUnitAndBuildingGroups(grouped, collapsedLabel) : grouped;
  const tabOrder: Array<AmenityParentGroup> = collapsedLabel ? [collapsedLabel, 'Policies'] : DEFAULT_TAB_ORDER;

  const hasPetPolicies = Boolean(petPolicies && (petPolicies.dog || petPolicies.cat));
  const hasParkingHighlight = Boolean(parkingHighlight && parkingHighlight.types.length > 0);
  const hasStructuredPolicies = Boolean(leaseTerms || hasPetPolicies || hasParkingHighlight);

  const availableTabs = tabOrder.filter((pg) => {
    if (pg === 'Policies') {
      return displayGroups.some((g) => g.parentGroup === 'Policies') || hasStructuredPolicies;
    }
    return displayGroups.some((g) => g.parentGroup === pg);
  });

  const resolvedTab = availableTabs.includes(activeTabGroup) ? activeTabGroup : availableTabs[0];
  const activeGroup = displayGroups.find((g) => g.parentGroup === resolvedTab);

  if (availableTabs.length === 0) {
    return null;
  }

  const tabs = availableTabs.map((pg) => ({ id: pg, label: pg }));

  const renderPoliciesContent = () => {
    const petPolicyRows = petPolicies ? buildPetPolicyRows(petPolicies) : [];

    // Only suppress string-based sub-groups when the structured data replacement exists
    const suppressedSubGroups: Array<AmenitySubGroup> = [];
    if (leaseTerms) suppressedSubGroups.push('Lease terms');
    if (hasPetPolicies) suppressedSubGroups.push('Pet policy');
    if (hasParkingHighlight) suppressedSubGroups.push('Parking policy');

    const remainingSubGroups = activeGroup?.subGroups.filter((sg) => !suppressedSubGroups.includes(sg.subGroup)) ?? [];

    return (
      <>
        {leaseTerms && (
          <div className={subGroupCard}>
            <div className={subGroupTitle}>
              <span className={subGroupIcon}>{getSubGroupIcon('Lease terms')}</span>
              Lease terms
            </div>
            <div className={leaseTermsContainer}>
              {parseLeaseTerms(leaseTerms).map((term) => (
                <span key={term} className={leaseTermPill}>
                  {term}
                </span>
              ))}
            </div>
          </div>
        )}

        {petPolicyRows.length > 0 && (
          <div className={subGroupCard}>
            <div className={subGroupTitle}>
              <span className={subGroupIcon}>{getSubGroupIcon('Pet policy')}</span>
              Pet policy
            </div>
            <ul className={policyList}>
              {petPolicyRows.map((row) => (
                <li key={row.label} className={amenityItem}>
                  <div className={policyRowInner}>
                    <span>{row.label}</span>
                    {row.badgeText && (
                      <Badge tone={row.badgeTone} size="sm">
                        {row.badgeText}
                      </Badge>
                    )}
                    {row.value && <span className={policyValue}>{row.value}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasParkingHighlight && parkingHighlight && (
          <div className={subGroupCard}>
            <div className={subGroupTitle}>
              <span className={subGroupIcon}>{getSubGroupIcon('Parking policy')}</span>
              Parking
            </div>
            <ul className={amenityList}>
              {parkingHighlight.types.map((type) => (
                <li key={type} className={amenityItem}>
                  {type}
                </li>
              ))}
            </ul>
          </div>
        )}

        {remainingSubGroups.map(renderSubGroupCard)}
      </>
    );
  };

  return (
    <div className={spokeContentContainer}>
      <Tabs
        tabs={tabs}
        activeTabId={resolvedTab}
        onTabChange={(id) => setActiveTabGroup(id as AmenityParentGroup)}
        ariaLabel="Amenity categories"
        idPrefix="amenity-tab"
      />

      <div role="tabpanel" aria-label={`${resolvedTab} amenities`} className={subGroupsContainer}>
        {resolvedTab === 'Policies' ? renderPoliciesContent() : activeGroup?.subGroups.map(renderSubGroupCard)}
      </div>
    </div>
  );
};
